import './styles.css';
import { createEngine, detectQuality } from './render/scene.js';
import { createProgress } from './journey/progress.js';
import { ACTS, actAt, actIndexAt, clamp01 } from './journey/acts.js';
import { createAmbient } from './audio/ambient.js';

const $ = (selector) => document.querySelector(selector);
const params = new URLSearchParams(location.search);
const seed = params.get('seed') || 'umbra-7';
const deepLinked = params.has('t');
const startT = clamp01(Number(params.get('t')));
const captureMode = params.get('capture') === '1';
const canvas = $('#world');
const quality = detectQuality(params);
if (captureMode && quality.tier > 0) {
  // Software WebGL at large internal buffers can expose incomplete frames to
  // the screenshot API. Bound the deterministic capture tier while retaining
  // the full-quality live desktop path.
  quality.steps = Math.min(quality.steps, 30);
  quality.pixelRatio = Math.min(quality.pixelRatio, 0.68);
}

function setHidden(element, hidden) {
  if (!element) return;
  element.hidden = Boolean(hidden);
  element.setAttribute('aria-hidden', String(Boolean(hidden)));
  if (hidden) element.setAttribute('inert', '');
  else element.removeAttribute('inert');
}

let engine;
try {
  engine = createEngine(canvas, { seed, quality, frozen: captureMode });
} catch (error) {
  console.error(error);
  setHidden($('#fallback'), false);
  setHidden($('#loading'), true);
  throw error;
}

const progress = createProgress({ reducedMotion: quality.reducedMotion });
const ambient = createAmbient();
progress.seek(startT);
progress.enabled = deepLinked;

let introOpen = !deepLinked;
let finaleOpen = false;
let activeAct = -1;
let arrivalPlayed = false;
let thrust = 0.25;
const keys = new Set();
const look = {
  x: 0,
  y: 0,
  targetX: 0,
  targetY: 0,
  dragging: false,
  pointerId: null,
  pointerType: 'mouse',
  lastX: 0,
  lastY: 0,
};

function smoothstep(a, b, value) {
  const x = Math.min(1, Math.max(0, (value - a) / (b - a)));
  return x * x * (3 - 2 * x);
}

function warpAt(t) {
  return smoothstep(0.70, 0.78, t) * (1 - smoothstep(0.85, 0.92, t));
}

function setSoundState(on) {
  const button = $('#sound');
  button.setAttribute('aria-pressed', String(on));
  button.setAttribute('aria-label', on ? 'Mute sound' : 'Enable sound');
  button.querySelector('i').textContent = on ? 'ON' : 'SOUND';
}

function setAutopilotState(on) {
  const button = $('#autopilot');
  button.setAttribute('aria-pressed', String(on));
  button.setAttribute('aria-label', on ? 'Disable autopilot' : 'Enable autopilot');
  button.querySelector('i').textContent = on ? 'FLYING' : 'AUTO';
}

function showAct(index, immediate = false) {
  if (index === activeAct) return;
  activeAct = index;
  const act = ACTS[index];
  const readout = $('.act-readout');
  const commit = () => {
    $('#act-index').textContent = String(index + 1).padStart(2, '0') + ' / 07';
    $('#act-kicker').textContent = act.kicker;
    $('#act-title').textContent = act.title;
    readout.classList.remove('changing');
    ambient.region(index);
  };
  if (immediate || quality.reducedMotion) commit();
  else {
    readout.classList.add('changing');
    setTimeout(commit, 220);
  }
}

function dismissIntro({ immediate = false } = {}) {
  if (!introOpen && $('#intro').hidden) return;
  introOpen = false;
  progress.enabled = true;
  const intro = $('#intro');
  if (immediate || quality.reducedMotion) {
    setHidden(intro, true);
  } else {
    intro.classList.add('leaving');
    intro.setAttribute('aria-hidden', 'true');
    intro.setAttribute('inert', '');
    setTimeout(() => {
      setHidden(intro, true);
      intro.classList.remove('leaving');
    }, 1250);
  }
}

function openFinale() {
  if (finaleOpen) return;
  finaleOpen = true;
  progress.stopGlide();
  setAutopilotState(false);
  setHidden($('#finale'), false);
  if (!arrivalPlayed) {
    ambient.arrival(7);
    arrivalPlayed = true;
  }
}

function hideFinale() {
  finaleOpen = false;
  setHidden($('#finale'), true);
}

function seek(value) {
  const t = clamp01(Number(value));
  progress.seek(t);
  if (t < 0.985) hideFinale();
  if (t >= 0.995 && !introOpen) openFinale();
  showAct(actIndexAt(t), true);
  return state();
}

async function begin() {
  dismissIntro();
  progress.target = Math.max(progress.target, 0.006);
  setSoundState(await ambient.start());
  ambient.region(actIndexAt(progress.t));
}

function replay() {
  hideFinale();
  arrivalPlayed = false;
  progress.seek(0);
  progress.enabled = true;
  progress.stopGlide();
  setAutopilotState(false);
  look.x = look.y = look.targetX = look.targetY = 0;
  showAct(0, true);
}

$('#begin').addEventListener('click', begin);
$('#sound').addEventListener('click', async () => setSoundState(await ambient.toggle()));
$('#autopilot').addEventListener('click', () => setAutopilotState(progress.toggleGlide()));
$('#replay').addEventListener('click', replay);

canvas.addEventListener('pointerdown', (event) => {
  if (introOpen || finaleOpen || (event.pointerType === 'mouse' && event.button !== 0)) return;
  look.dragging = true;
  look.pointerId = event.pointerId;
  look.pointerType = event.pointerType;
  look.lastX = event.clientX;
  look.lastY = event.clientY;
  canvas.setPointerCapture(event.pointerId);
});

addEventListener('pointermove', (event) => {
  if (!look.dragging || event.pointerId !== look.pointerId) return;
  const dx = event.clientX - look.lastX;
  const dy = event.clientY - look.lastY;
  look.lastX = event.clientX;
  look.lastY = event.clientY;
  const horizontalTouch = look.pointerType !== 'touch' || Math.abs(dx) > Math.abs(dy) * 1.15;
  if (horizontalTouch) {
    look.targetX = Math.min(1, Math.max(-1, look.targetX + dx * 0.0032));
    look.targetY = Math.min(0.78, Math.max(-0.78, look.targetY - dy * 0.0027));
  }
});

function endPointer(event) {
  if (!look.dragging || event.pointerId !== look.pointerId) return;
  look.dragging = false;
  look.pointerId = null;
}
addEventListener('pointerup', endPointer);
addEventListener('pointercancel', endPointer);

addEventListener('keydown', (event) => {
  if (event.target instanceof Element && event.target.matches('input, textarea, select, [contenteditable="true"]')) return;
  keys.add(event.code);
  if (event.key === 'g' || event.key === 'G') {
    if (!introOpen && !finaleOpen) setAutopilotState(progress.toggleGlide());
  }
  if (event.key === 'm' || event.key === 'M') ambient.toggle().then(setSoundState);
  if (event.key === 'Escape' && finaleOpen) hideFinale();
});
addEventListener('keyup', (event) => keys.delete(event.code));

function updateKeyboardLook(dt) {
  const speed = dt * 0.65;
  if (keys.has('KeyA')) look.targetX = Math.max(-1, look.targetX - speed);
  if (keys.has('KeyD')) look.targetX = Math.min(1, look.targetX + speed);
  if (keys.has('KeyW')) look.targetY = Math.min(0.78, look.targetY + speed * 0.75);
  if (keys.has('KeyS')) look.targetY = Math.max(-0.78, look.targetY - speed * 0.75);
  if (!look.dragging && !keys.has('KeyA') && !keys.has('KeyD')) look.targetX *= Math.max(0, 1 - dt * 0.38);
  if (!look.dragging && !keys.has('KeyW') && !keys.has('KeyS')) look.targetY *= Math.max(0, 1 - dt * 0.38);
}

function tick(dt) {
  updateKeyboardLook(dt);
  if (!introOpen && !finaleOpen) progress.update(dt);
  const t = progress.t;
  const damp = quality.reducedMotion ? 1 : 1 - Math.exp(-5.5 * dt);
  look.x += (look.targetX - look.x) * damp;
  look.y += (look.targetY - look.y) * damp;

  const warp = warpAt(t);
  engine.update({ progress: t, lookX: look.x, lookY: look.y, warp });
  showAct(actIndexAt(t));

  const percent = (t * 100).toFixed(2) + '%';
  $('#progress-fill').style.width = percent;
  $('#progress-pip').style.left = percent;
  $('#dash-progress').textContent = String(Math.round(t * 999)).padStart(3, '0');
  $('#vector').textContent = (112.4 * (1 - t) + 0.8).toFixed(1).padStart(5, '0');

  const targetThrust = 0.2 + Math.abs(progress.target - progress.t) * 8 + (progress.gliding ? 0.34 : 0) + warp * 0.5;
  thrust += (Math.min(1.2, targetThrust) - thrust) * Math.min(1, dt * 2.6);
  ambient.setThrust(thrust);

  if (t >= 0.995 && !introOpen && !finaleOpen) openFinale();
}

function computedVisible(element) {
  if (!element) return false;
  const style = getComputedStyle(element);
  const rect = element.getBoundingClientRect();
  return !element.hidden && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0.01 && rect.width > 0 && rect.height > 0;
}

function canvasReport() {
  const frame = engine.renderer.info.render.frame;
  return { ok: frame > 2 && canvas.width > 0 && canvas.height > 0, frame, width: canvas.width, height: canvas.height };
}

function state() {
  const act = actAt(progress.t);
  return {
    t: progress.t,
    target: progress.target,
    act: act.id,
    actTitle: act.title,
    seed,
    introOpen,
    finaleOpen,
    gliding: progress.gliding,
    sound: ambient.running,
    look: { x: look.x, y: look.y },
    quality,
    captureMode,
    visibility: {
      intro: computedVisible($('#intro')),
      finale: computedVisible($('#finale')),
      loading: computedVisible($('#loading')),
    },
    canvas: canvasReport(),
  };
}

const api = {
  seek,
  state,
  setLook(x = 0, y = 0) {
    look.x = look.targetX = Math.min(1, Math.max(-1, Number(x) || 0));
    look.y = look.targetY = Math.min(0.78, Math.max(-0.78, Number(y) || 0));
    return state();
  },
  dismissIntro() {
    dismissIntro({ immediate: true });
    return state();
  },
  toggleAutopilot() {
    const on = progress.toggleGlide();
    setAutopilotState(on);
    return on;
  },
};
window.__journey = api;
window.__vesper = api;

setSoundState(false);
setAutopilotState(false);
setHidden($('#fallback'), true);
setHidden($('#finale'), true);
showAct(actIndexAt(startT), true);
if (deepLinked) setHidden($('#intro'), true);
else setHidden($('#intro'), false);

engine.start(tick);
requestAnimationFrame(() => requestAnimationFrame(() => setHidden($('#loading'), true)));
